import type { ImageUploadResponse } from '@pulsify/shared/dto'
import { api, getAuthToken, unwrap } from '~/shared/lib/rpc'

// The image routes (apps/backend/src/routes/images.ts) read params/body manually
// (no zValidator), so the Hono RPC `$post/$put/$delete` input types do not expose
// `form`/`json`. We use the typed `$url()` builder for the correct base + path and
// drive the request with a plain fetch. The routes require auth, so the Bearer
// token is resolved live per request to avoid stale-token 401s.
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export function createImagesApi(resource: 'events' | 'listings') {
  const routes = api.api.images[resource]
  return {
    async upload(entityId: string, file: File): Promise<ImageUploadResponse> {
      const url = routes[':id'].$url({ param: { id: entityId } })
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: await authHeaders(),
      })
      return unwrap<ImageUploadResponse>(res)
    },

    async uploadMultiple(entityId: string, files: File[]): Promise<ImageUploadResponse[]> {
      const uploaded: ImageUploadResponse[] = []
      for (const file of files) {
        uploaded.push(await this.upload(entityId, file))
      }
      return uploaded
    },

    async delete(imageId: string): Promise<void> {
      const url = routes[':id'].$url({ param: { id: imageId } })
      const res = await fetch(url, {
        method: 'DELETE',
        headers: await authHeaders(),
      })
      await unwrap<{ success: boolean }>(res)
    },

    async reorder(entityId: string, imageIds: string[]): Promise<void> {
      const url = routes.reorder[':id'].$url({ param: { id: entityId } })
      const res = await fetch(url, {
        method: 'PUT',
        body: JSON.stringify({ imageIds }),
        headers: {
          ...(await authHeaders()),
          'Content-Type': 'application/json',
        },
      })
      await unwrap<{ success: boolean }>(res)
    },

    async copyFromEvent(targetEventId: string, sourceEventId: string): Promise<void> {
      const url = api.api.images.events[':id']['copy-from'][':sourceId'].$url({
        param: { id: targetEventId, sourceId: sourceEventId },
      })
      const res = await fetch(url, {
        method: 'POST',
        headers: await authHeaders(),
      })
      await unwrap<{ success: boolean }>(res)
    },
  }
}
